import { ComponentProps, ReactElement, forwardRef } from "react";
import { FontStyles } from "../../utils/font-styles.ts";
import { State } from "./input-states.ts";
import "../../../assets/fa-icons/css/fontawesome.css";
import "../../../assets/fa-icons/css/sharp-regular.css";

export type InputProps = ComponentProps<"input"> & {
  state: State;
  label?: string;
  labelSize?: "default" | "large";
  message?: string;
  renderLeadingIcon?: () => ReactElement;
  renderTrailingIcon?: () => ReactElement;
  inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search";
  showRequiredAsterisk?: boolean;
};

// Base styles
const baseInputStyle =
  "border rounded font-normal p-4 xs:p-3.5 w-full focus:outline focus:outline-1 " + FontStyles["body-l"];
const baseMessageStyle = "font-normal " + FontStyles["body-s"];

// Input variant styles
const defaultStyle =
  "text-slate placeholder:text-light-slate border-foggy-firth hover:border-neutral-300 focus:shadow-[0_0_0_4px] focus:outline-north-sea focus:shadow-north-sea-50";
const successStyle =
  "text-slate placeholder:text-light-slate border-glen shadow-[0_0_0_4px] focus:shadow-[0_0_0_4px] focus:outline-glen shadow-glen-50 focus:shadow-glen-50";
const errorStyle =
  "text-slate placeholder:text-light-slate border-brick shadow-[0_0_0_4px] focus:shadow-[0_0_0_4px] focus:outline-brick shadow-brick-50 focus:shadow-brick-50";
const disabledStyle = "text-neutral-200 placeholder:text-neutral-200 bg-cloud cursor-not-allowed border-foggy-firth";

// Message variant styles
const defaultMessage = "text-light-slate";
const successMessage = "text-glen";
const errorMessage = "text-brick";
const disabledMessage = "text-neutral-200";

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      state,
      id,
      placeholder,
      label,
      labelSize = "default",
      message,
      type,
      value,
      onChange,
      onBlur,
      renderLeadingIcon,
      renderTrailingIcon,
      // eslint-disable-next-line
      children, // descructuring children to avoid passing it to the input element in runtime which throws react error (input cannot have children)
      inputMode,
      showRequiredAsterisk = false,
      ...rest
    },
    ref,
  ) => {
    const inputStyle: string[] = [baseInputStyle];
    const messageStyle: string[] = [baseMessageStyle];

    switch (state) {
      case "default":
        inputStyle.push(defaultStyle);
        messageStyle.push(defaultMessage);
        break;
      case "success":
        inputStyle.push(successStyle);
        messageStyle.push(successMessage);
        break;
      case "error":
        inputStyle.push(errorStyle);
        messageStyle.push(errorMessage);
        break;
      case "disabled":
        inputStyle.push(disabledStyle);
        messageStyle.push(disabledMessage);
    }
    const leadingIcon = renderLeadingIcon ? renderLeadingIcon() : null;
    const trailingIcon = renderTrailingIcon ? renderTrailingIcon() : null;

    if (leadingIcon) {
      inputStyle.push("pl-10 xs:pl-10");
    }

    if (trailingIcon) {
      inputStyle.push("pr-10 xs:pr-10");
    }

    const labelStyle = `text-slate font-semibold ${labelSize === "large" ? FontStyles["body-md"] : FontStyles["body-s"]}`;

    return (
      <div className="flex flex-col w-full gap-1.5 md:gap-2">
        {label && (
          <label className={labelStyle} htmlFor={id}>
            {label}
            {showRequiredAsterisk && <span className="text-brick"> *</span>}
          </label>
        )}
        <div className="relative w-full">
          <input
            ref={ref}
            className={inputStyle.join(" ")}
            id={id}
            type={type}
            placeholder={placeholder}
            disabled={state === "disabled"}
            aria-invalid={state === "error"}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            inputMode={inputMode}
            {...rest}
          />
          {leadingIcon && (
            <div className="absolute inset-y-0 left-0 py-3.5 pl-1 w-10 flex items-center justify-center">
              {leadingIcon}
            </div>
          )}
          {trailingIcon && (
            <div className="absolute inset-y-0 right-0 py-3.5 pr-1 w-10 flex items-center justify-center">
              {trailingIcon}
            </div>
          )}
        </div>
        {message && <span className={messageStyle.join(" ")}>{message}</span>}
      </div>
    );
  },
);

Input.displayName = "Input";
